import { apiRequest } from "./backendApi";

/** Mirrors `connectorTypeSchema` in server `agent.schema.ts`. */
export type ConnectorType =
  | "openai_completions"
  | "openai_responses"
  | "mcp_stdio"
  | "mcp_sse"
  | "mcp_http"
  | "bank_faq";

export type ValidationState = "valid" | "warning" | "error" | "not_validated";

export type ConnectionFamily = "openai_responses" | "openai_completions" | "mcp" | "managed_model";
export type McpTransportUi = "sse" | "http";

export type Agent = {
  id: string;
  name: string;
  connector_type: ConnectorType;
  connectorLabel: string;
  enabled: boolean;
  validationState: ValidationState;
  validationMessage: string;
  validatedAt: string;
  createdAt: string;
  updatedAt: string;
  /** Raw connector config from API (for editing). */
  config: Record<string, unknown>;
  /** Lines shown on agent cards — tailored to connector type. */
  summaryLines: string[];
};

export type AgentFormValues = {
  agentId: string;
  name: string;
  connectionFamily: ConnectionFamily;
  mcpTransport: McpTransportUi;
  model: string;
  endpoint: string;
  apiKeyRef: string;
  requestTimeoutMs: string;
  temperature: string;
  maxOutputTokens: string;
  command: string;
  argsText: string;
  url: string;
  headersJson: string;
  defaultTool: string;
};

export const DEFAULT_OPENAI_RESPONSES_ENDPOINT = "https://api.openai.com/v1/responses";
export const DEFAULT_OPENAI_COMPLETIONS_ENDPOINT = "https://api.openai.com/v1/chat/completions";

export function emptyAgentForm(): AgentFormValues {
  return {
    agentId: "",
    name: "",
    connectionFamily: "openai_responses",
    mcpTransport: "sse",
    model: "gpt-4.1-mini",
    endpoint: DEFAULT_OPENAI_RESPONSES_ENDPOINT,
    apiKeyRef: "",
    requestTimeoutMs: "30000",
    temperature: "0.3",
    maxOutputTokens: "700",
    command: "",
    argsText: "",
    url: "",
    headersJson: "{}",
    defaultTool: "",
  };
}

export function isManagedModelAgent(agent: Pick<Agent, "connector_type">): boolean {
  return agent.connector_type === "bank_faq";
}

export function managedModelProviderLabel(config: Record<string, unknown>): string {
  const baseUrl = typeof config.base_url === "string" ? config.base_url.toLowerCase() : "";
  if (baseUrl.includes("openrouter.ai")) return "OpenRouter";
  if (baseUrl.includes("openai.com")) return "OpenAI";
  if (baseUrl) return "Custom API";
  const keyRef = typeof config.api_key_ref === "string" ? config.api_key_ref.toUpperCase() : "";
  if (keyRef.includes("OPENROUTER")) return "OpenRouter";
  if (keyRef.includes("OPENAI")) return "OpenAI";
  return "Model provider";
}

export function managedModelModeLabel(mode: unknown): string {
  return mode === "rag" ? "Knowledge-assisted (RAG)" : "Direct model";
}

export function connectorTypeToLabels(
  connector_type: ConnectorType,
): { connectionFamily: ConnectionFamily; mcpTransport: McpTransportUi } {
  if (connector_type === "bank_faq") {
    return { connectionFamily: "managed_model", mcpTransport: "http" };
  }
  if (connector_type === "openai_responses") {
    return { connectionFamily: "openai_responses", mcpTransport: "sse" };
  }
  if (connector_type === "openai_completions") {
    return { connectionFamily: "openai_completions", mcpTransport: "sse" };
  }
  /* Legacy stdio agents open as MCP with SSE selected — operator migrates connection details before save. */
  if (connector_type === "mcp_stdio") return { connectionFamily: "mcp", mcpTransport: "sse" };
  if (connector_type === "mcp_sse") return { connectionFamily: "mcp", mcpTransport: "sse" };
  return { connectionFamily: "mcp", mcpTransport: "http" };
}

export function resolveConnectorType(form: AgentFormValues): ConnectorType {
  if (form.connectionFamily === "openai_responses") return "openai_responses";
  if (form.connectionFamily === "openai_completions") return "openai_completions";
  if (form.mcpTransport === "sse") return "mcp_sse";
  return "mcp_http";
}

export function connectorLabel(type: ConnectorType): string {
  switch (type) {
    case "bank_faq":
      return "Managed model";
    case "openai_responses":
      return "OpenAI Responses API";
    case "openai_completions":
      return "OpenAI Completions API";
    case "mcp_stdio":
      return "MCP (stdio)";
    case "mcp_sse":
      return "MCP (SSE)";
    case "mcp_http":
      return "MCP (HTTP)";
    default:
      return type;
  }
}

function truncateText(text: string, maxLen: number): string {
  const t = text.trim();
  if (t.length <= maxLen) return t;
  return `${t.slice(0, Math.max(0, maxLen - 1))}…`;
}

function buildSummaryLines(connector_type: ConnectorType, config: Record<string, unknown>): string[] {
  const c = config || {};
  switch (connector_type) {
    case "bank_faq": {
      const provider = managedModelProviderLabel(c);
      const model = typeof c.model === "string" ? c.model : "";
      const mode = managedModelModeLabel(c.mode);
      const lines = [`${provider} model`];
      if (model) lines.push(`Model: ${model}`);
      lines.push(mode);
      return lines;
    }
    case "openai_responses":
    case "openai_completions": {
      const lines = [connectorLabel(connector_type)];
      const model = typeof c.model === "string" ? c.model : "";
      if (model) lines.push(`Model: ${model}`);
      const ep = typeof c.endpoint === "string" ? c.endpoint : "";
      if (ep) lines.push(truncateText(ep, 52));
      return lines;
    }
    case "mcp_stdio": {
      const cmd = typeof c.command === "string" ? c.command : "";
      const tool = typeof c.default_tool === "string" ? c.default_tool : "";
      const lines = ["MCP (stdio)"];
      lines.push(cmd ? `Command: ${truncateText(cmd, 44)}` : "Command: —");
      lines.push(tool ? `Tool: ${tool}` : "Tool: —");
      return lines;
    }
    case "mcp_sse":
    case "mcp_http": {
      const transport = connector_type === "mcp_sse" ? "SSE" : "HTTP";
      const url = typeof c.url === "string" ? c.url : "";
      const tool = typeof c.default_tool === "string" ? c.default_tool : "";
      const lines = [`MCP (${transport})`];
      lines.push(url ? truncateText(url, 52) : "URL: —");
      lines.push(tool ? `Tool: ${tool}` : "Tool: —");
      return lines;
    }
    default:
      return [connector_type];
  }
}

type AgentApiRow = {
  id: string;
  name: string;
  connector_type: ConnectorType;
  config: Record<string, unknown>;
  enabled: boolean;
  validation_state: "unknown" | "valid" | "warning" | "failed";
  created_at: string;
  updated_at: string;
};

type AgentListResponse = {
  data: AgentApiRow[];
  count: number;
};

type ValidationApiResponse = {
  status: "valid" | "warning" | "failed" | "skipped";
  checks: Array<{ name: string; passed: boolean; blocking: boolean; message?: string }>;
  executed_at: string;
};

const asString = (value: unknown): string => (typeof value === "string" ? value : "");

function toValidationState(value: AgentApiRow["validation_state"]): ValidationState {
  if (value === "valid") return "valid";
  if (value === "warning") return "warning";
  if (value === "failed") return "error";
  return "not_validated";
}

function defaultValidationMessage(row: AgentApiRow): string {
  if (row.validation_state === "unknown") return "Not yet validated.";
  return `Validation state: ${row.validation_state}.`;
}

export function toAgent(row: AgentApiRow, validationMessage?: string, validatedAt?: string): Agent {
  const config = (row.config ?? {}) as Record<string, unknown>;
  return {
    id: row.id,
    name: row.name,
    connector_type: row.connector_type,
    connectorLabel: connectorLabel(row.connector_type),
    enabled: row.enabled,
    validationState: toValidationState(row.validation_state),
    validationMessage: validationMessage ?? defaultValidationMessage(row),
    validatedAt: validatedAt ?? row.updated_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    config,
    summaryLines: buildSummaryLines(row.connector_type, config),
  };
}

export function agentToForm(agent: Agent): AgentFormValues {
  const { connectionFamily, mcpTransport } = connectorTypeToLabels(agent.connector_type);
  const c = agent.config || {};
  const defaultParams =
    typeof c.default_params === "object" && c.default_params !== null
      ? (c.default_params as Record<string, unknown>)
      : {};

  const base: AgentFormValues = {
    agentId: agent.id,
    name: agent.name,
    connectionFamily,
    mcpTransport,
    model: asString(c.model) || "gpt-4.1-mini",
    endpoint:
      asString(c.endpoint) ||
      (connectionFamily === "openai_completions"
        ? DEFAULT_OPENAI_COMPLETIONS_ENDPOINT
        : DEFAULT_OPENAI_RESPONSES_ENDPOINT),
    apiKeyRef: asString(c.api_key_ref),
    requestTimeoutMs:
      typeof c.request_timeout_ms === "number" ? String(c.request_timeout_ms) : "30000",
    temperature:
      typeof defaultParams.temperature === "number" ? String(defaultParams.temperature) : "0.3",
    maxOutputTokens:
      typeof defaultParams.max_tokens === "number" ? String(defaultParams.max_tokens) : "700",
    command: asString(c.command),
    argsText: Array.isArray(c.args) ? (c.args as unknown[]).map(String).join("\n") : "",
    url: asString(c.url),
    headersJson:
      typeof c.headers === "object" && c.headers !== null
        ? JSON.stringify(c.headers, null, 2)
        : "{}",
    defaultTool: asString(c.default_tool),
  };

  return base;
}

function parseArgsLines(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function parseHeadersJson(text: string): Record<string, string> {
  const t = text.trim();
  if (!t) return {};
  try {
    const parsed = JSON.parse(t) as unknown;
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      throw new Error("Headers must be a JSON object.");
    }
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).map(([k, v]) => [k, String(v)]),
    );
  } catch (e) {
    throw new Error(e instanceof Error ? e.message : "Invalid headers JSON.");
  }
}

function positiveIntString(raw: string, fallback: number): number {
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

/** Builds POST /api/agents body (strict discriminated union on server). */
export function buildAgentCreateBody(id: string, form: AgentFormValues): Record<string, unknown> {
  const connector_type = resolveConnectorType(form);
  const name = form.name.trim();
  const timeoutMs = positiveIntString(form.requestTimeoutMs, 30_000);

  if (connector_type === "openai_responses") {
    return {
      id,
      name,
      enabled: true,
      connector_type,
      config: {
        model: form.model.trim(),
        endpoint: form.endpoint.trim(),
        api_key_ref: form.apiKeyRef.trim(),
        request_timeout_ms: timeoutMs,
        default_params: {
          temperature: Number(form.temperature),
          max_tokens: Number(form.maxOutputTokens),
        },
      },
    };
  }

  if (connector_type === "openai_completions") {
    return {
      id,
      name,
      enabled: true,
      connector_type,
      config: {
        mode: "completions",
        model: form.model.trim(),
        endpoint: form.endpoint.trim(),
        api_key_ref: form.apiKeyRef.trim(),
        request_timeout_ms: timeoutMs,
        default_params: {
          temperature: Number(form.temperature),
          max_tokens: Number(form.maxOutputTokens),
        },
      },
    };
  }

  if (connector_type === "mcp_stdio") {
    return {
      id,
      name,
      enabled: true,
      connector_type,
      config: {
        command: form.command.trim(),
        args: parseArgsLines(form.argsText),
        env: {},
        default_tool: form.defaultTool.trim(),
        request_timeout_ms: timeoutMs,
      },
    };
  }

  const headers = parseHeadersJson(form.headersJson);
  return {
    id,
    name,
    enabled: true,
    connector_type,
    config: {
      url: form.url.trim(),
      headers,
      default_tool: form.defaultTool.trim(),
      request_timeout_ms: timeoutMs,
    },
  };
}

/** Builds PUT /api/agents/:id body; merged server-side with existing row. */
export function buildAgentUpdateBody(form: AgentFormValues): Record<string, unknown> {
  const created = buildAgentCreateBody("placeholder", form) as {
    name: string;
    connector_type: ConnectorType;
    config: Record<string, unknown>;
  };
  return {
    name: created.name,
    connector_type: created.connector_type,
    config: created.config,
  };
}

const makeAgentId = (name: string): string =>
  `${name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 72)}-${Date.now()}`;

export const agentsApi = {
  async listAgents(): Promise<Agent[]> {
    const res = await apiRequest<AgentListResponse>("GET", "/api/agents");
    if (!res || !Array.isArray(res.data)) {
      throw new Error(
        "Agents list response was missing a data array. The request may have hit a non-API server or an outdated proxy; confirm /api/agents returns JSON with { data, count }.",
      );
    }
    return res.data.map((row) => toAgent(row));
  },

  async createAgent(form: AgentFormValues): Promise<Agent> {
    const id = form.agentId.trim() || makeAgentId(form.name);
    const body = buildAgentCreateBody(id, form);
    const created = await apiRequest<AgentApiRow>("POST", "/api/agents", { body });
    return toAgent(created, "Not yet validated after latest changes.");
  },

  async updateAgent(id: string, form: AgentFormValues): Promise<Agent> {
    const body = buildAgentUpdateBody(form);
    const updated = await apiRequest<AgentApiRow>("PUT", `/api/agents/${encodeURIComponent(id)}`, {
      body,
    });
    return toAgent(updated, "Updated. Re-run validation to refresh connection status.");
  },

  async updateAgentName(id: string, name: string): Promise<Agent> {
    const updated = await apiRequest<AgentApiRow>("PUT", `/api/agents/${encodeURIComponent(id)}`, {
      body: { name: name.trim() },
    });
    return toAgent(updated);
  },

  async setAgentEnabled(id: string, enabled: boolean): Promise<Agent> {
    const updated = await apiRequest<AgentApiRow>("PUT", `/api/agents/${encodeURIComponent(id)}`, {
      body: { enabled },
    });
    return toAgent(updated);
  },

  async deleteAgent(id: string): Promise<void> {
    await apiRequest<void>("DELETE", `/api/agents/${encodeURIComponent(id)}`);
  },

  async validateAgent(id: string): Promise<Agent> {
    const validation = await apiRequest<ValidationApiResponse>(
      "POST",
      `/api/agents/${encodeURIComponent(id)}/validate`,
      { body: { auto_refine: false } },
    );
    const row = await apiRequest<AgentApiRow>("GET", `/api/agents/${encodeURIComponent(id)}`);

    const failedMessages = validation.checks
      .filter((c) => !c.passed && c.message)
      .map((c) => c.message as string);
    const stateMessage =
      failedMessages.join(" ") ||
      validation.checks.find((c) => c.message)?.message ||
      `Validation completed: ${validation.status}.`;

    return toAgent(row, stateMessage, validation.executed_at);
  },
};
